import { useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.observability';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import {
  CopyToCastItResultEnum,
  CopyToCastItStatusEnum,
  GetCopyToCastItStudioXJobStatusDoc,
} from '@minecraft.graphql-operations';
import { PollingHandler, PollingInstanceStatus } from './types';

/**
 * Hook that handles polling for Copy to Cast It Studio X job statuses.
 * Should be used once at the app level, inside the PollingProvider.
 */
export const useCopyToCastItStudioPolling = () => {
  const apollo = useApolloClient();

  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();

  const showSuccessMessage = useCallback(() => {
    showMessage({
      message: t('casting:copyToCastItStudio.copySuccess'),
      color: 'success',
    });
    captureHeapEvent({ name: HEAP_EVENT_NAMES.COPY_TO_CAST_IT_STUDIO_SUCCESS });
  }, [showMessage, t]);

  const showErrorMessage = useCallback(() => {
    showMessage({
      message: t('casting:copyToCastItStudio.copyError.noneCopied'),
      color: 'danger',
    });
    captureHeapEvent({ name: HEAP_EVENT_NAMES.COPY_TO_CAST_IT_STUDIO_FAILED_TO_COPY });
  }, [showMessage, t]);

  const showNoMediaToCopyMessage = useCallback(() => {
    showMessage({
      message: t('casting:copyToCastItStudio.noMediaToCopy'),
      color: 'warning',
    });
    captureHeapEvent({ name: HEAP_EVENT_NAMES.COPY_TO_CAST_IT_STUDIO_NO_MEDIA_TO_COPY });
  }, [showMessage, t]);

  const pollCopyToCastItStudio: PollingHandler = useCallback(
    async ({ instance }) => {
      try {
        const res = await apollo.query({
          query: GetCopyToCastItStudioXJobStatusDoc,
          variables: {
            input: { jobId: instance.id },
          },
          fetchPolicy: 'no-cache',
        });

        if (!res.data?.getCopyToCastItStudioXJobStatus) {
          showErrorMessage();
          return { ...instance, status: PollingInstanceStatus.FAILED };
        }

        const jobStatus = res.data.getCopyToCastItStudioXJobStatus.status;
        const jobResult = res.data.getCopyToCastItStudioXJobStatus.result;
        const jobStatusValue = String(jobStatus ?? '').toUpperCase();
        const jobResultValue = String(jobResult ?? '').toUpperCase();

        const isStatusSuccess = jobStatusValue === CopyToCastItStatusEnum.Success;
        const isStatusFailure = jobStatusValue === CopyToCastItStatusEnum.Failed;
        const isStatusInProgress = jobStatusValue === CopyToCastItStatusEnum.InProgress;
        const isResultAllCopied = jobResultValue === CopyToCastItResultEnum.AllCopied;
        const isResultNoneCopied = jobResultValue === CopyToCastItResultEnum.NoneCopied;
        const isResultFailure = jobResultValue === CopyToCastItResultEnum.Error;
        const isResultInProgress = jobResultValue === CopyToCastItResultEnum.InProgress;

        if (isStatusInProgress || isResultInProgress) {
          return { ...instance, status: PollingInstanceStatus.IN_PROGRESS };
        }

        if (isStatusFailure || isResultFailure) {
          throw new Error('Copy to Cast It Studio X job failed');
        }

        if (isStatusSuccess && isResultNoneCopied) {
          showNoMediaToCopyMessage();
          return { ...instance, status: PollingInstanceStatus.COMPLETED };
        }

        if ((!isStatusFailure && isResultAllCopied) || isStatusSuccess) {
          showSuccessMessage();
          return { ...instance, status: PollingInstanceStatus.COMPLETED };
        }

        return { ...instance, status: PollingInstanceStatus.IN_PROGRESS };
      } catch (error) {
        showErrorMessage();
        captureExceptionEvent(error, {
          location: 'useCopyToCastItStudioPolling - pollCopyToCastItStudio',
        });
        return { ...instance, status: PollingInstanceStatus.FAILED };
      }
    },
    [showSuccessMessage, showErrorMessage, showNoMediaToCopyMessage, apollo]
  );

  return useMemo(() => ({ pollCopyToCastItStudio }), [pollCopyToCastItStudio]);
};
